import React, { memo, useEffect, useRef, useState } from 'react';

import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { NON_BREAKING_SPACE } from './constants';
import type { AnnouncerProps } from './types';

function Announcer({
	message: originalMessage,
	liveMode = 'polite',
	shouldAnnounce = true,
	testId,
}: AnnouncerProps): React.JSX.Element {
	const previousMessage = useRef('');
	const setAlternatePolite = useRef(false);
	const addSuffix = useRef(false);

	const [oldMessage, setOldMessage] = useState('');
	const [newMessage, setNewMessage] = useState('');

	useEffect(() => {
		let message = shouldAnnounce ? originalMessage : NON_BREAKING_SPACE;
		if (previousMessage.current === message) {
			addSuffix.current = !addSuffix.current;
			if (addSuffix.current) {
				message += NON_BREAKING_SPACE;
			}
		}
		setOldMessage(setAlternatePolite.current ? '' : message);
		setNewMessage(setAlternatePolite.current ? message : '');
		if (shouldAnnounce) {
			setAlternatePolite.current = !setAlternatePolite.current;
		}
		previousMessage.current = originalMessage;
	}, [originalMessage, shouldAnnounce]);

	// 2 aria-live containers needed to prevent bugs in browsers that will stop
	// announcing updates to the same div if these are too repetitive.
	return (
		<VisuallyHidden>
			<div data-testid={testId}>
				<div role="status" aria-live={liveMode} aria-relevant="additions text" aria-atomic="true">
					{oldMessage}
				</div>
				<div role="status" aria-live={liveMode} aria-relevant="additions text" aria-atomic="true">
					{newMessage}
				</div>
			</div>
		</VisuallyHidden>
	);
}

const MemoizedAnnouncer: React.MemoExoticComponent<typeof Announcer> = memo(Announcer);
export default MemoizedAnnouncer;
